import React, { useEffect, useState, useRef } from 'react'
import { useI18n } from '../../i18n'
import scanService from '../../services/scanService'
import { getActiveOrgId } from '../../utils/orgUtils'
import { RefreshCw, Eye } from 'lucide-react'
import ScanDetailsModal from '../ScanDetailsModal'
import { componentScope } from '../../lib/cssScope'
import { Button } from '@/components/ui/Button'
import cardStyles from '../../styles/components/assets/ActiveScansCard.module.css'

interface ActiveScanRow {
  jobId: string
  taskId?: string
  name?: string
  target?: string
  status?: string
  progress?: number
  startedAt?: string | null
  reportId?: string | null
}

function statusBadgeClass(status: string): string {
  const s = status.toLowerCase()
  if (s === 'running') return cardStyles.statusRunning
  if (s === 'queued' || s === 'pending') return cardStyles.statusQueued
  if (s === 'done' || s === 'completed') return cardStyles.statusDone
  if (s === 'failed') return cardStyles.statusFailed
  return cardStyles.statusDefault
}

function isLiveScanStatus(status?: string): boolean {
  const s = String(status || '').toLowerCase()
  return s === 'running' || s === 'queued' || s === 'pending'
}

function normalizeProgress(value: unknown): number {
  const n = typeof value === 'number' ? value : parseInt(String(value ?? 0), 10)
  if (!Number.isFinite(n)) return 0
  return Math.min(100, Math.max(0, n))
}

async function enrichActiveProgress(rows: ActiveScanRow[]): Promise<ActiveScanRow[]> {
  if (!rows.length) return rows
  return Promise.all(
    rows.map(async (row) => {
      try {
        const res = await scanService.providerGetStatus(row.jobId, { force: true })
        if (res.success && res.data) {
          const status = String(res.data.status || row.status || 'unknown').toLowerCase()
          return {
            ...row,
            status,
            progress: normalizeProgress(res.data.progress ?? row.progress),
            target: res.data.target || row.target,
            taskId: res.data.taskId || row.taskId,
            reportId: res.data.reportId ?? row.reportId,
            startedAt: res.data.startedAt || row.startedAt,
          }
        }
      } catch (err) {
        console.warn('[ActiveScansCard] progress fetch failed:', row.jobId, err)
      }
      return { ...row, progress: normalizeProgress(row.progress) }
    }),
  )
}

const ActiveScansCard: React.FC = () => {
  const { t } = useI18n()
  const [rows, setRows] = useState<ActiveScanRow[]>([])
  const [lastScan, setLastScan] = useState<ActiveScanRow | null>(null)
  const [loading, setLoading] = useState(false)
  const STALE_RUNNING_MS = 24 * 60 * 60 * 1000
  const [expanded, setExpanded] = useState(false)
  const [selectedScan, setSelectedScan] = useState<ActiveScanRow | null>(null)
  const [scanDetails, setScanDetails] = useState<any>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const timerRef = useRef<number | null>(null)

  const refresh = async () => {
    setLoading(true)
    try {
      const orgId = getActiveOrgId()
      if (!orgId) {
        setRows([])
        setLoading(false)
        return
      }

      const jobs = await scanService.providerListScans(orgId)
      const allScans = Array.isArray(jobs) ? jobs : []
      const now = Date.now()

      const mapJob = (j: any): ActiveScanRow => ({
        jobId: String(j.id || j.scanId || ''),
        taskId: j.taskId,
        name: j.scanName || j.name || 'Unknown Scan',
        target: j.targets || j.target || 'N/A',
        status: String(j.status || 'unknown').toLowerCase(),
        progress: typeof j.progress === 'number' ? j.progress : parseInt(String(j.progress || 0), 10),
        startedAt: j.startedAt || j.createdAt || null,
        reportId: j.reportId || null,
      })

      const activeMapped = allScans
        .filter((j: any) => {
          const status = String(j.status || '').toLowerCase()
          if (!(status === 'running' || status === 'queued' || status === 'pending')) return false
          const started = new Date(j.startedAt || j.createdAt || 0).getTime()
          if (Number.isFinite(started) && now - started > STALE_RUNNING_MS) return false
          return true
        })
        .map(mapJob)

      const active = await enrichActiveProgress(
        activeMapped.map((r) => ({ ...r, progress: normalizeProgress(r.progress) })),
      )

      const completed = allScans
        .filter((j: any) => {
          const status = String(j.status || '').toLowerCase()
          return status === 'done' || status === 'completed' || status === 'failed' || status === 'stopped'
        })
        .map(mapJob)
        .sort((a, b) => {
          const ta = new Date(a.startedAt || 0).getTime()
          const tb = new Date(b.startedAt || 0).getTime()
          return tb - ta
        })

      setRows(active)
      setLastScan(completed[0] || null)

      if (active.length > 0) {
        setExpanded(true)
      }
    } catch (error) {
      console.error('Failed to fetch active scans:', error)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  useEffect(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
    const intervalMs = rows.length > 0 ? 10000 : 60000
    timerRef.current = window.setInterval(() => {
      void refresh()
    }, intervalMs)
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [rows.length > 0])

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleString()
    } catch {
      return dateStr
    }
  }

  const handleViewDetails = async (row: ActiveScanRow) => {
    setSelectedScan(row)
    setLoadingDetails(true)
    try {
      const res = await scanService.providerGetStatus(row.jobId, { force: true })
      if (res.success && res.data) {
        setScanDetails({
          taskId: res.data.taskId || row.taskId || '—',
          name: res.data.name || row.name || 'Unknown Scan',
          config: res.data.config || '—',
          target: res.data.target || row.target || '—',
          scanner: res.data.scanner || 'OpenVAS',
          status: res.data.status || row.status || 'unknown',
          progress: res.data.progress || row.progress || 0,
          reportId: res.data.reportId || row.reportId || null,
          startedAt: res.data.startedAt || row.startedAt || null,
          finishedAt: res.data.finishedAt || null,
          resultCount: res.data.resultCount || null,
        })
      } else {
        setScanDetails({
          taskId: row.taskId || '—',
          name: row.name || 'Unknown Scan',
          config: '—',
          target: row.target || '—',
          scanner: 'OpenVAS',
          status: row.status || 'unknown',
          progress: row.progress || 0,
          reportId: row.reportId || null,
          startedAt: row.startedAt || null,
          finishedAt: null,
          resultCount: null,
        })
      }
    } catch (error) {
      console.error('Failed to fetch scan details:', error)
      setScanDetails({
        taskId: row.taskId || '—',
        name: row.name || 'Unknown Scan',
        config: '—',
        target: row.target || '—',
        scanner: 'OpenVAS',
        status: row.status || 'unknown',
        progress: row.progress || 0,
        reportId: row.reportId || null,
        startedAt: row.startedAt || null,
        finishedAt: null,
        resultCount: null,
      })
    } finally {
      setLoadingDetails(false)
    }
  }

  return (
    <div className={`${componentScope('ActiveScansCard')} ${cardStyles.card}`}>
      <div className={cardStyles.header}>
        <h3 className={cardStyles.title}>{t('assets.scans.activeScans', 'Active Scans')}</h3>
        <Button
          type="button"
          color="secondary"
          size="small"
          onClick={refresh}
          disabled={loading}
          icon={<RefreshCw className={loading ? cardStyles.iconSpin : undefined} aria-hidden />}
          title={t('assets.scans.updateStatus', 'Update Status')}
        >
          {t('assets.scans.updateStatus', 'Update Status')}
        </Button>
      </div>

      {lastScan && rows.length === 0 ? (
        <div className={cardStyles.lastScanBanner}>
          <p className={cardStyles.lastScanLabel}>{t('assets.scans.lastRun', 'Last scan')}</p>
          <p className={cardStyles.scanName}>{lastScan.name}</p>
          <p className={cardStyles.targetRow}>
            <span className={cardStyles.targetLabel}>{t('assets.scans.target', 'Target')}:</span>
            <span className={cardStyles.targetValue}> {lastScan.target}</span>
            {' · '}
            <span className={`${cardStyles.statusBadge} ${statusBadgeClass(lastScan.status || 'unknown')}`}>
              {lastScan.status?.toUpperCase() || 'UNKNOWN'}
            </span>
          </p>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className={cardStyles.empty}>
          {lastScan
            ? t('assets.scans.noActiveScansWithLast', 'No active scans. Last run shown above.')
            : t('assets.scans.noActiveScans', 'No active scans')}
        </div>
      ) : (
        <div className={cardStyles.list}>
          {rows.slice(0, expanded ? rows.length : 3).map((row) => {
            const live = isLiveScanStatus(row.status)
            const progressPct = normalizeProgress(row.progress)
            return (
              <div key={row.jobId} className={cardStyles.scanBlock}>
                <div className={cardStyles.scanHeader}>
                  <div className={cardStyles.scanMain}>
                    <p className={cardStyles.scanName}>{row.name || 'Unknown Scan'}</p>
                    <p className={cardStyles.targetRow}>
                      <span className={cardStyles.targetLabel}>{t('assets.scans.target', 'Target')}:</span>
                      <span className={cardStyles.targetValue}> {row.target}</span>
                    </p>
                  </div>
                  <span className={`${cardStyles.statusBadge} ${statusBadgeClass(row.status || 'unknown')}`}>
                    {row.status?.toUpperCase() || 'UNKNOWN'}
                    {live ? ` · ${progressPct}%` : ''}
                  </span>
                </div>

                {live ? (
                  <div className={cardStyles.progressBlock}>
                    <div className={cardStyles.progressMeta}>
                      <span className={cardStyles.progressLabel}>{t('assets.scans.progress', 'Progress')}</span>
                      <span className={cardStyles.progressValue}>{progressPct}%</span>
                    </div>
                    <div className={cardStyles.progressTrack}>
                      <div className={cardStyles.progressFill} style={{ width: `${progressPct}%` }} />
                    </div>
                  </div>
                ) : null}

                {row.startedAt ? (
                  <p className={cardStyles.startedRow}>
                    <span className={cardStyles.startedLabel}>{t('assets.scans.startedAt', 'Started')}:</span>
                    <span> {formatDate(row.startedAt)}</span>
                  </p>
                ) : null}

                <div className={cardStyles.actions}>
                  <Button
                    type="button"
                    color="outline"
                    size="small"
                    onClick={() => handleViewDetails(row)}
                    className={cardStyles.linkButton}
                    disabled={loadingDetails}
                    icon={<Eye className="h-3 w-3" aria-hidden />}
                    iconPosition="end"
                  >
                    {t('assets.scans.viewDetails', 'View Details')}
                  </Button>
                </div>
              </div>
            )
          })}
          {rows.length > 3 && (
            <div className={cardStyles.toggleRow}>
              <Button
                type="button"
                color="outline"
                size="small"
                onClick={() => setExpanded(!expanded)}
                className={cardStyles.toggleLink}
              >
                {expanded
                  ? t('assets.scans.showLess', 'Show Less')
                  : t('assets.scans.showMore', 'Show {count} more', { count: rows.length - 3 })}
              </Button>
            </div>
          )}
        </div>
      )}

      <ScanDetailsModal
        open={selectedScan !== null}
        onClose={() => {
          setSelectedScan(null)
          setScanDetails(null)
        }}
        data={scanDetails}
      />
    </div>
  )
}

export default ActiveScansCard
